"use client"

import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import "./ConfirmModal.css"

/**
 * Reusable confirmation modal popup.
 * Used for logout, delete, and any action that needs user confirmation.
 * Dismissible via Cancel button, Escape key, or clicking outside.
 */
export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  message,
  title = "Confirm",
  confirmButtonLabel = "Confirm",
  pendingButtonLabel = "Please wait…",
  errorMessage,
  /** "danger" (default, red) | "brand" (purple primary actions) */
  confirmVariant = "danger",
  showCancel = true,
}) {
  const confirmBtnRef = useRef(null)

  useEffect(() => {
    if (!isOpen) return

    const handleEsc = (e) => {
      if (e.key === "Escape" && !isPending) {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEsc)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", handleEsc)
      document.body.style.overflow = ""
    }
  }, [isOpen, isPending, onClose])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isPending) {
      onClose()
    }
  }

  const handleConfirm = () => {
    if (isPending) return
    onConfirm()
  }

  if (!isOpen) return null

  return createPortal(
    <div
      className="confirmModalOverlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-body"
    >
      <div className="confirmModalBox" onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-modal-title" className="confirmModalTitle">
          {title}
        </h2>
       <p
  id="confirm-modal-body"
  className="confirmModalBody"
  style={{ whiteSpace: "pre-line" }}
>
  {message } 
</p>
        {errorMessage ? (
          <p className="confirmModalError" role="alert">
            {errorMessage}
          </p>
        ) : null}
        <div className="confirmModalActions">
          {showCancel && (
            <button
              type="button"
              className="confirmModalBtn confirmModalBtn--secondary"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </button>
          )}
          <button
            ref={confirmBtnRef}
            type="button"
            className={`confirmModalBtn confirmModalBtn--primary confirmModalBtn--${confirmVariant}`}
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? pendingButtonLabel : confirmButtonLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
